import React, { useEffect, useState } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Icon,
  Upload,
  message,
  List,
  Typography,
  Tooltip,
  Checkbox,
  Spin,
  Alert,
  Result
} from 'antd'
import {
  CheckCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'

import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'

import './styles.scss'
import { api } from '../../../../../../services'
import { general } from '../../../../../../constants'
import { bytesToSize } from '../../../../../../utils'
import actions from '../../../../../../store/actions'
import history from '../../../../../../history'
import { useTranslation } from 'react-i18next'
import DocumentSelectorModal from '../DocumentSelectorModal';

const { Text } = Typography

const formFields = {
  end_goal: {
    value: 'End Goal',
    description: 'The ultimate objective or desired outcome of the scenario.'
  },
  problem_statement: {
    value: 'Problem Statement',
    description:
      'A concise description of the challenge or issue to be addressed in the scenario.'
  },
  learner_role: {
    value: 'Learner Role',
    description:
      'The specific role or position the learner is expected to assume within the scenario.'
  },
  depth: {
    value: 'Depth',
    description: 'The number of layers of scenarios within the metamaze'
  },
  no_of_options: {
    value: 'No of Options',
    description:
      'The number of alternative choices or paths available to the learner within the scenario.'
  }
}

const generationStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  error: 'error'
}

const AiMazeGeneration = ({
  form,
  onMazeGenerated,
  onClose,
  status: generatedStatus
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [fileList, setFileList] = useState([])
  const [fileError, setFileError] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [fileUploadStatus, setFileUploadStatus] = useState({})
  const [isMediaLibrarySelected, setIsMediaLibrarySelected] = useState(false)
  const [status, setStatus] = useState('')

  const dispatch = useDispatch()
  const library = useSelector(state => state.library)
  const user = useSelector(state => state.user)
  const maze = useSelector(state => state.mazeBuilder)
  const { uploadProgress, uploadSuccess } = library

  const [isDocumentSelectorVisible, setIsDocumentSelectorVisible] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isSpecificDocSelected, setIsSpecificDocSelected] = useState(false);

  useEffect(() => {
    if (generatedStatus) setStatus(handleGenerationStatus(generatedStatus))

    if (generatedStatus === generationStatus.in_progress) {
      checkMazeStatus()
    }
  }, [generatedStatus])

  useEffect(() => {
    checkFileError()
  }, [fileList])

  // Add these handler functions:
  const handleDocumentSelect = (documents) => {
    setSelectedDocuments(documents);
    console.log("documents handleDocumentSelect: ", documents)
    console.log("isSpecificDocSelected handleDocumentSelect: ", isSpecificDocSelected)
    // message.success(`Selected ${documents.length} document(s)`);
    if (isSpecificDocSelected) {
      const files = documents.map((doc) => {
        try {
          const url = new URL(doc.link);
          return url.pathname.substring(1); // remove leading slash
        } catch (error) {
          console.error("Invalid document link:", doc.link);
          return "";
        }
      });
      console.log("files isSpecificDocSelected: ", files)
      setFileList(files.filter(Boolean)); // filter out empty strings
    }
  };

  const handleSpecificDocCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsSpecificDocSelected(checked);

    if (checked) {
      setIsMediaLibrarySelected(false);
      setIsDocumentSelectorVisible(true);
    } else {
      setSelectedDocuments([]);
      setFileList([]);
    }
  };

  // Also add handler for organization library checkbox
  const handleMediaLibraryChange = () => {
    const newValue = !isMediaLibrarySelected;
    setIsMediaLibrarySelected(newValue);

    if (newValue) {
      setIsSpecificDocSelected(false); // Uncheck specific documents
      setSelectedDocuments([]);
    }
  };

  const handleRemoveDocument = (docId) => {
    if (selectedDocuments.length > 0) {
      setSelectedDocuments(selectedDocuments.filter(doc => doc.id !== docId));
    }
  };


  const checkMazeStatus = () => {
    setStatus(generationStatus.in_progress)
    api.maze
      .getMazeById(maze.mazeId)
      .then(res => {
        const mazeObj = res.data.data
        if (mazeObj && mazeObj.generated_status === 'in_progress') {
          _.delay(checkMazeStatus, 20000)
        } else if (mazeObj && mazeObj.generated_status === 'completed') {
          onMazeGenerated()
          setStatus('')
        } else if (mazeObj && mazeObj.generated_status === 'error') {
          setStatus(generationStatus.error)
        }
      })
      .catch(error => {
        console.error('Error checking maze status:', error)
        setStatus(generationStatus.error)
      })
  }

  const generateMaze_updated = async (values, fileList) => {
    const {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options
    } = values;

    const { mazeId, mazeTitle } = maze;
    const { active_organization_id, id, username, email } = user.info;

    const payload = {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options,
      title: mazeTitle,
      programme_id: mazeId,
      user_id: id,
      email,
      username,
      org_id: active_organization_id,
      ...(!isMediaLibrarySelected && !isSpecificDocSelected && {
        file_list: fileList
      }),
      ...(isSpecificDocSelected && selectedDocuments.length > 0 && {
        document_ids: selectedDocuments.map(doc => doc.id)
      })
    };

    try {
      const res = await api.maze.generateMazeByAi(payload);
      if (res.data) {
        const status = res.data.status;
        if (status === 'success') {
          checkMazeStatus();
        }
      }
    } catch (err) {
      console.log(err);
      message.error('An error occurred while generating the maze.');
      setStatus(generationStatus.error);
    }
  };

  // Update the validation in handleOk:
  const handleOk_updated = async e => {
    e.preventDefault();
    form.validateFields(async (errors, values) => {
      // Update validation to include specific documents check
      if (!isMediaLibrarySelected && !isSpecificDocSelected && fileError) return;

      if (isSpecificDocSelected && selectedDocuments.length === 0) {
        message.error('Please select at least one document');
        return;
      }

      if (!errors) {
        try {
          if (isMediaLibrarySelected) {
            setIsGenerating(true);
            await generateMaze(values);
          } else if (isSpecificDocSelected) {
            setIsGenerating(true);
            await generateMaze(values);
          } else {
            setIsUploading(true);
            const newFiles = await uploadFiles(fileList);
            setIsUploading(false);
            setIsGenerating(true);
            const files = newFiles.map(item => item.path);
            await generateMaze(values, files);
          }
        } catch (err) {
          console.log(err);
          message.error('An error occurred during the process.');
        } finally {
          setIsUploading(false);
          setIsGenerating(false);
        }
      }
    });
  };

  const handleOk = async e => {
    e.preventDefault()
    form.validateFields(async (errors, values) => {
      console.log("log  values handleOk", values)
      console.log("log  isMediaLibrarySelected handleOk", isMediaLibrarySelected)
      console.log("log  isSpecificDocSelected handleOk", isSpecificDocSelected)
      console.log("log  fileList handleOk", fileList)

      if (!isMediaLibrarySelected && !isSpecificDocSelected && fileError) return
      if (!errors) {
        try {
          if (isMediaLibrarySelected) {
            // Case 1: From media library
            setIsGenerating(true)
            await generateMaze(values)
          } else if (isSpecificDocSelected) {
            // Case 2: Specific doc selected (no upload needed)
            console.log("inside elseif ", isSpecificDocSelected)
            setIsGenerating(true)
            await generateMaze(values)
          } else {
            // Case 3: Upload flow
            setIsUploading(true)
            const newFiles = await uploadFiles(fileList)
            setIsUploading(false)

            setIsGenerating(true)
            const files = newFiles.map(item => item.path)
            await generateMaze(values, files)
          }
        } catch (err) {
          console.error(err)
          message.error('An error occurred during the process.')
        } finally {
          setIsUploading(false)
          setIsGenerating(false)
        }
      }
    })
  }

  const uploadFiles = async files => {
    setUploadedFiles([])
    setFileUploadStatus({})
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'uploading'
        }))
        const res = await dispatch(
          actions.library.upload(file.originFileObj, {
            ai_language: 'en-US',
            isAiEnabled: true
          })
        )
        const newFiles = [...uploadedFiles, res]
        setUploadedFiles(newFiles)
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'success'
        }))

        return newFiles
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error)
        message.error(`Failed to upload ${file.name}`)
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'error'
        }))
        throw error
      }
    }
  }


  const generateMaze = async (values, files) => {
    console.log("files log: ", files)
    console.log("fileList log: ", fileList)
    const {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options
    } = values

    const { mazeId, mazeTitle } = maze

    const { active_organization_id, id, username, email } = user.info

    const payload = {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options,
      title: mazeTitle,
      programme_id: mazeId,
      user_id: id,
      email,
      username,
      org_file: true,
      org_id: active_organization_id,
      ...((!isMediaLibrarySelected || isSpecificDocSelected) && {
        file_list: files && files.length > 0 ? files : fileList
      })
    }

    try {
      const res = await api.maze.generateMazeByAi(payload)
      if (res.data) {
        const status = res.data.status

        if (status === 'success') {
          checkMazeStatus()
        }
      }
    } catch (err) {
      console.log(err)
      message.error('An error occurred while generating the maze.')
      setStatus(generationStatus.error)
    }
  }

  const handleGenerationStatus = input => {
    const { in_progress, completed, error } = generationStatus
    const newStatus =
      input === 'in_progress'
        ? in_progress
        : input === 'completed'
          ? completed
          : error

    return newStatus
  }

  const checkFileError = () => {
    if (fileList.length === 0) {
      setFileError('Please upload at least one supported file (.pdf, .doc, .docx, .ppt, .pptx, .txt).')
    } else if (fileList.length > 5) {
      setFileError(
        'You can upload a maximum of 5 files. For more, please use the media library.'
      )
    } else {
      setFileError('')
    }
  }

  const handleFileChange = info => {
    let newFileList = [...info.fileList]

    // Filter files to only accept supported file types
    newFileList = newFileList.filter(file => {
      const isAcceptedType = Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)

      if (!isAcceptedType) {
        const acceptedExtensions = Object.values(ACCEPTED_FILE_TYPES).join(', ')
        message.error(`${file.name} is not a supported file type. Accepted types: ${acceptedExtensions}`)
        return false
      }
      return true
    })

    setFileList(newFileList)
  }

  const { getFieldDecorator } = form

  const checkedCompletUpload = item => {
    const filterSuccessFiles = _.filter(
      uploadSuccess,
      ele => ele.id === item.id
    )
    return !(uploadSuccess.length && filterSuccessFiles.length)
  }

  const handleModalClose = () => {
    const { mazeId } = maze

    if (status === generationStatus.in_progress || isGenerating) {
      dispatch(actions.maze.getMazeById(mazeId))
      history.push(`/maze/${mazeId}/edit`)
    } else {
      onClose()
    }
  }
  const { t } = useTranslation()

  const renderFileStatus = () => {
    return (
      <List
        header="Upload File Status"
        itemLayout="horizontal"
        dataSource={fileList}
        renderItem={file => (
          <List.Item>
            <List.Item.Meta
              avatar={<Icon type="paper-clip" />}
              title={file.name}
              description={
                <>
                  {fileUploadStatus[file.name] === 'uploading' && (
                    <Text type="secondary">
                      <Icon type="loading" /> {t('general:uploading')}
                    </Text>
                  )}
                  {fileUploadStatus[file.name] === 'success' && (
                    <Text type="success">
                      <Icon type="check-circle" /> {t('v4:uploaded_successful')}
                    </Text>
                  )}
                  {fileUploadStatus[file.name] === 'error' && (
                    <Text type="danger">
                      <Icon type="close-circle" /> {t('v4:upload_failed')}
                    </Text>
                  )}
                </>
              }
            />
          </List.Item>
        )}
      />
    )
  }

  const ACCEPTED_FILE_TYPES = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'text/plain': '.txt'
  }

  const GenerationFormView = () => {
    return (
      <Form layout="vertical" onSubmit={handleOk}>
        <div className="ai-maze-form-modal__body">
          {!isMediaLibrarySelected && !isSpecificDocSelected && (
            <>
              <Form.Item
                label={t('v4:upload_only_five_mb')} // You might want to update your translation key
                colon={false}
                required={true}
              >
                <Upload
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false} // Prevent auto upload
                >
                  <Button>
                    <Icon type="upload" /> {t('buttons:select_files')}
                  </Button>
                </Upload>
                {fileError && (
                  <div style={{ color: 'red', marginTop: '8px' }}>
                    {fileError}
                  </div>
                )}
              </Form.Item>
              <b>OR</b>&nbsp;&nbsp;
            </>
          )}

          {/* check box for selection */}

          {/* Hide Organization Library checkbox when Specific Documents is selected */}
          {!isSpecificDocSelected && (
            <Form.Item colon={false} required={false}>
              <Checkbox
                checked={isMediaLibrarySelected}
                onChange={() =>
                  setIsMediaLibrarySelected(!isMediaLibrarySelected)
                }
              >
                <b className="ai-maze-form-label">
                  {t('v4:docs_from_org_library')}
                  &nbsp;{t('v4:docs_from_org_library_sop')}
                </b>
              </Checkbox>
            </Form.Item>
          )}

          {!isSpecificDocSelected && !isMediaLibrarySelected && (
            <>
              <b>OR</b>&nbsp;&nbsp;
            </>
          )}

          {!isMediaLibrarySelected && (
            <>
              <Form.Item colon={false} required={false}>
                <Checkbox
                  checked={isSpecificDocSelected}
                  onChange={handleSpecificDocCheckboxChange}
                >
                  <b className="ai-maze-form-label">
                    Select Specific Document(s) (SOP)
                  </b>
                </Checkbox>
                {isSpecificDocSelected && selectedDocuments.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Selected Documents ({selectedDocuments.length}):</Text>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setIsDocumentSelectorVisible(true)}
                      >
                        Manage Selection
                      </Button>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {selectedDocuments.map((doc, index) => (
                        <div
                          key={doc.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            fontSize: '13px',
                            maxWidth: '100%'
                          }}
                        >
                          <span style={{ marginRight: '6px', fontWeight: '600', color: '#1890ff' }}>
                            {index + 1}.
                          </span>
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginRight: '8px'
                          }}>
                            {doc.name}
                          </span>
                          <Icon
                            type="close"
                            style={{
                              cursor: 'pointer',
                              color: '#ff4d4f',
                              fontSize: '12px'
                            }}
                            onClick={() => handleRemoveDocument(doc.id)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Item>
            </>
          )}
        </div>

        {!isMediaLibrarySelected && (
          <div style={{ marginBottom: '20px' }}>
            {(isUploading || isGenerating) && renderFileStatus()}

            {uploadProgress.length > 0 &&
              uploadSuccess.length !== uploadProgress.length &&
              uploadProgress.map((item, ind) =>
                item.percent < 100 ? (
                  <div key={item.uid} className="upload-modal__item">
                    <div className="upload-modal-item">
                      <div className="upload-modal-item__percent-border">
                        <div
                          className={`upload-modal-item__percent ${item.status === general.CANCELED
                            ? 'upload-modal-item__percent__cancel'
                            : ''
                            }`}
                          style={{ width: item.percent + '%' }}
                        />
                      </div>
                    </div>

                    {item.status !== general.CANCELED && (
                      <div className="upload-modal-item__weight">
                        {checkedCompletUpload(item) && (
                          <span>
                            {bytesToSize(item.loaded)} of{' '}
                            {bytesToSize(item.total)}
                          </span>
                        )}
                        {!checkedCompletUpload(item) && (
                          <span className="upload-modal-item__weight__complete">
                            {uploadedFiles[ind] && uploadedFiles[ind].removed
                              ? 'Removed'
                              : 'Completed'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : null
              )}
          </div>
        )}

        <Form.Item
          label={
            <span className="ai-maze-form-label">
              <span>{formFields.end_goal.value}</span>
              <Tooltip title={formFields.end_goal.description}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          colon={false}
          required={false}
        >
          {getFieldDecorator('end_goal', {
            rules: [{ required: true, message: 'Please input end goal' }]
          })(<Input.TextArea autoSize />)}
        </Form.Item>
        <Form.Item
          label={
            <span className="ai-maze-form-label">
              <span>{formFields.problem_statement.value}</span>
              <Tooltip title={formFields.problem_statement.description}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          colon={false}
          required={false}
        >
          {getFieldDecorator('problem_statement', {
            rules: [
              { required: true, message: 'Please input problem statement' }
            ]
          })(<Input.TextArea autoSize />)}
        </Form.Item>
        <Form.Item
          label={
            <span className="ai-maze-form-label">
              <span>{formFields.learner_role.value}</span>
              <Tooltip title={formFields.learner_role.description}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          colon={false}
          required={false}
        >
          {getFieldDecorator('learner_role', {
            rules: [{ required: true, message: 'Please input learner role' }]
          })(<Input.TextArea autoSize />)}
        </Form.Item>
        <Form.Item
          label={
            <span className="ai-maze-form-label">
              <span>{formFields.depth.value}</span>
              <Tooltip title={formFields.depth.description}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          colon={false}
          required={false}
        >
          {getFieldDecorator('depth', {
            rules: [{ required: true, message: 'Please select depth' }],
            initialValue: 2 // Set default value
          })(
            <Select>
              {[2, 3, 4, 5, 6, 7, 8].map(value => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item
          label={
            <span className="ai-maze-form-label">
              <span>{formFields.no_of_options.value}</span>
              <Tooltip title={formFields.no_of_options.description}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          colon={false}
          required={false}
        >
          {getFieldDecorator('number_of_options', {
            rules: [
              { required: true, message: 'Please select number of options' }
            ],
            initialValue: 2 // Set default value
          })(
            <Select>
              {[2, 3, 4].map(value => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        {status && status === generationStatus.error && (
          <Alert
            message="Error"
            description={t('v4:failed_maze_generate')}
            type="error"
            closable
            showIcon
            icon={<CloseCircleOutlined />}
          />
        )}

        <br />

        <Form.Item>
          <Button
            htmlType="submit"
            shape="round"
            size="large"
            type="primary"
            onClick={handleOk}
            loading={isUploading || isGenerating}
          >
            {isUploading
              ? 'Uploading...'
              : isGenerating
                ? 'Generating...'
                : 'Generate MetaMaze'}
          </Button>
        </Form.Item>
      </Form>
    )
  }

  const GenerationStatusView = () => {
    const { in_progress, completed, error } = generationStatus

    // Customize loading icon for Spin
    const loadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />

    if (status === in_progress) {
      return (
        <Result
          icon={<Spin indicator={loadingIcon} />}
          title={t('v4:generate_metamaze')}
          subTitle={t('v4:metamaze_being_generated')}
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => history.push('/')}
            >
              Go To Training
            </Button>
          }
        />
      )
    }

    if (status === completed) {
      return (
        <Result
          status="success"
          title={t('v4:metamaze_generate_successfully')}
          subTitle={t('v4:metamaze_with_proceed')}
          icon={<CheckCircleOutlined />}
        />
      )
    }

    return null
  }

  const { active_organization_id } = user.info;

  return (
    <>
      <Modal
        visible
        centered
        footer={null}
        closable={false}
        className="ai-maze-form-modal"
        width={800}
      >
        <div className="ai-maze-form-modal__header">
          <p>{t('v4:ai_metamaze_generate')}</p>
          <Icon type="close" onClick={handleModalClose} />
        </div>

        {status && status !== generationStatus.error
          ? GenerationStatusView()
          : GenerationFormView()}
      </Modal>
      <DocumentSelectorModal
        visible={isDocumentSelectorVisible}
        onClose={() => setIsDocumentSelectorVisible(false)}
        onSelect={handleDocumentSelect}
        activeOrg={active_organization_id}
      />
    </>

  )
}

// Wrap AiMazeGeneration component with Form.create() to access form methods via props
const WrappedAiMazeGeneration = Form.create({ name: 'ai_maze_generation' })(
  AiMazeGeneration
)

export default WrappedAiMazeGeneration
